/**
 * Equipment health is the one place colour carries meaning (UX-003 §3.7) — and
 * it is ALWAYS paired with a shape and a text label, never colour alone
 * (UX-004 §2.2): filled circle ok, triangle warning, square fault, hollow circle
 * offline, dashed circle unmonitored. Operational modifiers ride as text.
 */
import type { ReactNode } from "react";

export type HealthState =
  | "ok"
  | "warning"
  | "fault"
  | "offline"
  | "unmonitored"
  | "partial";

export type HealthModifier = "standby" | "isolated" | "under_maintenance";

const HEALTH_LABEL: Record<HealthState, string> = {
  ok: "OK",
  warning: "Warning",
  fault: "Fault",
  offline: "Offline",
  unmonitored: "Not monitored",
  partial: "Partial",
};

const MODIFIER_LABEL: Record<HealthModifier, string> = {
  standby: "Standby",
  isolated: "Isolated",
  under_maintenance: "Under maintenance",
};

export function healthLabel(state: HealthState): string {
  return HEALTH_LABEL[state];
}

/** A health indicator: shape + colour + (optional) label; modifiers as text. */
export function HealthBadge(props: {
  state: HealthState;
  modifiers?: HealthModifier[];
  showLabel?: boolean;
}): ReactNode {
  const mods = props.modifiers ?? [];
  return (
    <span
      className="sk-health"
      data-state={props.state}
      role="img"
      aria-label={healthLabel(props.state)}
    >
      <span className="sk-health__glyph" aria-hidden="true" />
      {props.showLabel !== false ? (
        <span className="sk-health__label">{HEALTH_LABEL[props.state]}</span>
      ) : null}
      {mods.map((m) => (
        <span className="sk-health__mod" key={m}>
          {MODIFIER_LABEL[m]}
        </span>
      ))}
    </span>
  );
}

/** Just the shape+colour dot (for dense contexts), with an accessible label. */
export function HealthDot(props: { state: HealthState }): ReactNode {
  return (
    <span
      className="sk-health sk-health--dot"
      data-state={props.state}
      role="img"
      aria-label={healthLabel(props.state)}
    >
      <span className="sk-health__glyph" aria-hidden="true" />
    </span>
  );
}
